import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, LifeBuoy } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const f = "h-11 border-white/10 bg-[#07090E]";
const EMPTY = { name: "", email: "", reason: "", subject: "", message: "" };

const Sent = ({ number, again }) => (
  <div className="panel p-8 text-center" data-testid="contact-success">
    <CheckCircle2 className="mx-auto h-10 w-10 text-[#00F5D4]" />
    <h2 className="mt-4 font-display text-2xl font-bold">Ticket #{number} received</h2>
    <p className="mt-2 text-sm text-slate-400">We sent a confirmation to your email. Our reply will arrive there too.</p>
    <button className="btn-ghost mt-6" onClick={again} data-testid="contact-new-ticket">Open another ticket</button>
  </div>
);

export default function Contact() {
  const { user } = useAuth();
  const [reasons, setReasons] = useState([]);
  const [v, setV] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);
  useEffect(() => { api.get("/support/reasons").then((r) => setReasons(r.data)); }, []);
  useEffect(() => { if (user) setV((p) => ({ ...p, name: p.name || user.stage_name || user.name || "", email: p.email || user.email })); }, [user]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", v);
      setSent(data.number);
      setV({ ...EMPTY, name: v.name, email: v.email });
    } catch (er) { toast.error(errMsg(er)); } finally { setBusy(false); }
  };
  return (
    <section className="mx-auto max-w-2xl px-4 pb-32 pt-28 md:px-8" data-testid="contact-page">
      <div className="label-mono flex items-center gap-2"><LifeBuoy className="h-3.5 w-3.5" />Support</div>
      <h1 className="mb-3 mt-3 font-display text-4xl font-black uppercase sm:text-5xl">Contact us</h1>
      <p className="mb-10 text-slate-400">Questions, payments, technical issues, copyright or press: open a ticket and we'll answer by email.</p>
      {sent ? <Sent number={sent} again={() => setSent(null)} /> : (
        <form onSubmit={submit} className="panel space-y-3 p-6" data-testid="contact-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input required placeholder="Your name" value={v.name} onChange={set("name")} className={f} data-testid="contact-name" />
            <Input required type="email" placeholder="Email for our reply" value={v.email} onChange={set("email")} className={f} data-testid="contact-email" />
          </div>
          <select required value={v.reason} onChange={set("reason")} className={`${f} w-full rounded-md border px-3 text-sm`} data-testid="contact-reason">
            <option value="">Reason…</option>
            {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <Input required placeholder="Subject" value={v.subject} onChange={set("subject")} className={f} data-testid="contact-subject" />
          <Textarea required minLength={10} rows={6} placeholder="How can we help?" value={v.message} onChange={set("message")} className="border-white/10 bg-[#07090E]" data-testid="contact-message" />
          <button className="btn-hot w-full" disabled={busy} data-testid="contact-submit">{busy ? "Sending…" : "Send ticket"}</button>
        </form>
      )}
    </section>
  );
}
